import type { ImportFieldNode } from '@erp/rpc';

/**
 * Column vocabulary a stateful table accepts. Narrower than SchemaFieldType,
 * since every column becomes a real Postgres column.
 */
export const STATEFUL_COLUMN_TYPES = [
  'string',
  'integer',
  'float',
  'numeric',
  'boolean',
  'date',
] as const;

export type StatefulColumnType = (typeof STATEFUL_COLUMN_TYPES)[number];

export interface ColumnDraft {
  id: string;
  name: string;
  type: StatefulColumnType;
  description: string;
}

export const MIN_DESCRIPTION_CHARS = 10;
/** Mirrors MAX_KEY_COLUMNS in StatefulTableService. */
export const MAX_KEY_COLUMNS = 8;
export const MAX_DESCRIPTION_CHARS = 500;
const MAX_NAME_CHARS = 100;
const COLUMN_NAME_RE = /^[a-z_][a-z0-9_]*$/i;

/** Column types a schema field lowers to. Anything else is carried as text. */
const FIELD_TYPE_TO_COLUMN_TYPE: Record<string, StatefulColumnType> = {
  string: 'string',
  text: 'string',
  enum: 'string',
  url: 'string',
  file: 'string',
  image: 'string',
  integer: 'integer',
  float: 'float',
  numeric: 'numeric',
  currency: 'numeric',
  boolean: 'boolean',
  date: 'date',
  datetime: 'date',
  time: 'string',
};

export function columnTypeForFieldType(fieldType: string): StatefulColumnType {
  return FIELD_TYPE_TO_COLUMN_TYPE[fieldType] ?? 'string';
}

export function emptyColumnDraft(): ColumnDraft {
  return {
    id: crypto.randomUUID(),
    name: '',
    type: 'string',
    description: '',
  };
}

/** Postgres-safe column name derived from a source header. */
export function normalizeColumnName(raw: string): string {
  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (cleaned.length === 0) return 'column';
  // A leading digit is not a legal identifier start.
  return /^[0-9]/.test(cleaned) ? `col_${cleaned}` : cleaned;
}

export interface InferredColumns {
  drafts: ColumnDraft[];
  /**
   * Source headers dropped because they normalize onto a column already taken —
   * "Planner Code" and "planner code" both become `planner_code`. Surfaced so
   * the wizard can say which columns the table will not carry, rather than
   * letting a later sync be the first hint.
   */
  skippedFieldNames: string[];
}

/**
 * Turns inferred schema fields into column drafts. Only top-level fields are
 * used: a stateful table is flat, so a nested container has no column to
 * become. Descriptions are pre-filled to clear the required minimum — the
 * column matcher reads them, so they have to say something.
 */
export function columnDraftsFromImportFields(
  fields: ImportFieldNode[],
): InferredColumns {
  const seen = new Set<string>();
  const drafts: ColumnDraft[] = [];
  const skippedFieldNames: string[] = [];

  for (const field of fields) {
    const name = normalizeColumnName(field.fieldName);
    if (seen.has(name)) {
      skippedFieldNames.push(field.fieldName);
      continue;
    }
    seen.add(name);
    drafts.push({
      id: crypto.randomUUID(),
      name,
      type: columnTypeForFieldType(field.fieldType),
      description:
        field.description?.trim() ||
        `Values from the "${field.fieldName}" column of the source file.`,
    });
  }

  return { drafts, skippedFieldNames };
}

export type ColumnDraftValidation =
  | { ok: true; columns: Array<Omit<ColumnDraft, 'id'>> }
  | { ok: false; errors: Record<string, string>; message?: string };

/**
 * Validates drafts against what the stateful table service will accept, so the
 * wizard blocks before a round trip rather than surfacing a 400.
 */
export function validateColumnDrafts(
  drafts: ColumnDraft[],
  /** Draft ids of the key columns, in key order. */
  keyColumnIds: readonly string[],
): ColumnDraftValidation {
  const errors: Record<string, string> = {};

  if (drafts.length === 0) {
    return { ok: false, errors, message: 'Add at least one column.' };
  }

  const seen = new Set<string>();
  for (const draft of drafts) {
    const name = draft.name.trim();
    if (name.length === 0 || name.length > MAX_NAME_CHARS) {
      errors[draft.id] =
        `A column name must be 1–${MAX_NAME_CHARS} characters.`;
      continue;
    }
    if (!COLUMN_NAME_RE.test(name)) {
      errors[draft.id] =
        'Use a letter or underscore followed by letters, digits, or underscores.';
      continue;
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      errors[draft.id] = `"${name}" is already used by another column.`;
      continue;
    }
    seen.add(key);

    const description = draft.description.trim();
    if (
      description.length < MIN_DESCRIPTION_CHARS ||
      description.length > MAX_DESCRIPTION_CHARS
    ) {
      errors[draft.id] =
        `Describe this column in ${MIN_DESCRIPTION_CHARS}–${MAX_DESCRIPTION_CHARS} characters — the column matcher reads it.`;
    }
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  if (keyColumnIds.length === 0) {
    return {
      ok: false,
      errors,
      message: 'Choose at least one column for the lookup key.',
    };
  }
  if (keyColumnIds.length > MAX_KEY_COLUMNS) {
    return {
      ok: false,
      errors,
      message: `A key can span at most ${MAX_KEY_COLUMNS} columns.`,
    };
  }
  const keyColumns = keyColumnIds.map((id) =>
    drafts.find((draft) => draft.id === id),
  );
  if (keyColumns.some((column) => column === undefined)) {
    return {
      ok: false,
      errors,
      message: 'A key column was removed — choose the key again.',
    };
  }

  return {
    ok: true,
    columns: drafts.map((draft) => ({
      name: draft.name.trim(),
      type: draft.type,
      description: draft.description.trim(),
    })),
  };
}

/**
 * A readable mapping name from a source file name: extension dropped,
 * separators turned into spaces. "planner_codes-2026.csv" → "Planner codes
 * 2026". A default worth typing over, not the AI suggestion.
 */
export function mappingNameFromFileName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.[^.\s]+$/, '');
  const words = withoutExtension.replace(/[_\-.]+/g, ' ').trim();
  if (words.length === 0) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Slug suggestion for a mapping name, matching the contract's slug rule. */
export function slugifyMappingName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug.slice(0, 255) : '';
}
